import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSettingsState } from "@/context/SettingsStateContext";
import config from "@/config";
import { useSettings } from "@/context/SettingsContext";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";

const WORDPRESS_PROXY_URL = `${config.supabase.functionsUrl}/wordpress-proxy`;

export default function useWordpressSettings() {
  const {
    settings: wpSettings,
    initiateWordPressAuth,
    disconnect: disconnectWordPress,
  } = useWordPress();
  const {
    styleInput,
    subjects,
    setIsSaving,
    setIsAuthenticating,
    showAuthDialog,
    setShowAuthDialog,
    setWpUrl,
    wpUsername,
    setWpUsername,
    wpPassword,
    setWpPassword,
    setConnectionError,
    setConnectionDiagnostics,
    directWpSettings,
    setDirectWpSettings,
    wpFormatOpen,
    setWpFormatOpen,
    setHtmlTemplate,
    setWpPublishStatus,
    setIsConnecting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  } = useSettingsState();
  const {
    postingFrequency,
    formattemplate,
    imagePrompt,
    imageModel,
    negativePrompt,
    weeklyPlanningDay,
    contentPolicies,
    isLoading: settingsLoading,
    updateSettingsInDatabase,
  } = useSettings();
  const { currentWebsite } = useWebsites();

  const wordPressSettingsSection = useMemo(() => <div className="space-y-4"></div>, []);

  // Then fix the improved function to check if WordPress is properly configured
  const isWordPressConfigured = () => {
    console.log("Checking WordPress configuration with direct settings and context...");

    // Check if we have direct settings from the database
    if (
      directWpSettings &&
      directWpSettings.wp_url &&
      directWpSettings.wp_username &&
      directWpSettings.wp_application_password
    ) {
      console.log("WordPress is configured based on direct database settings");
      return true;
    }

    // Fall back to context values if direct settings are not available
    if (
      wpSettings &&
      wpSettings.wp_url &&
      wpSettings.wp_username &&
      wpSettings.wp_application_password
    ) {
      console.log("WordPress is configured based on context values");
      return true;
    }

    console.log(
      "WordPress is not configured (neither direct settings nor context has values)"
    );
    return false;
  };

  /**
   * Checks if WordPress is fully connected with all required properties
   * @returns {boolean} True if WordPress is connected and has valid credentials
   */
  const isWordPressConnected = () => {
    // Log WordPress settings for debugging
    console.log(
      "Checking WordPress connection status with settings:",
      wpSettings
        ? {
            has_settings: !!wpSettings,
            has_url: !!wpSettings?.wp_url,
            has_username: !!wpSettings?.wp_username,
            has_password: !!(wpSettings?.wp_application_password?.length > 0),
            is_connected_flag: wpSettings?.is_connected,
            id: wpSettings?.id,
            website_id: wpSettings?.website_id,
          }
        : "No settings found"
    );

    // The actual check
    const isConnected = !!(
      wpSettings &&
      wpSettings.wp_url &&
      wpSettings.wp_username &&
      wpSettings.wp_application_password &&
      wpSettings.is_connected
    );

    console.log("WordPress connection result:", isConnected);
    return isConnected;
  };

  // Fix the refresh function to manually check WordPress connection
  const refreshWordPressSettings = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    toast.info("Checking WordPress connection...");
    console.log("Manually refreshing WordPress settings for website:", currentWebsite.id);

    try {
      const { data, error } = await supabase
        .from("wordpress_settings")
        .select("*")
        .eq("website_id", currentWebsite.id)
        .single();

      if (error) {
        if (error.code !== "PGRST116") {
          // Not the "no rows returned" error
          console.error("Error fetching WordPress settings from DB:", error);
          toast.error("Error checking WordPress connection");
        } else {
          console.log("No WordPress settings found in database for this website");
          toast.error("WordPress is not configured for this website");
        }
      } else if (data) {
        console.log("WordPress settings refreshed from database:", {
          url: data.wp_url,
          isConnected: data.is_connected,
          hasUsername: !!data.wp_username,
          hasPassword: !!data.wp_application_password,
        });

        // Store the WordPress settings
        setDirectWpSettings(data);

        if (data.is_connected) {
          toast.success("WordPress connection is active");
        } else {
          toast.warning("WordPress connection exists but is inactive");
        }
      }
    } catch (e) {
      console.error("Exception checking WordPress settings:", e);
      toast.error("Error checking WordPress connection");
    }
  };

  // Helper function to get WordPress admin edit URL for a post
  const getWordPressEditUrl = (postId: number): string => {
    if (!wpSettings?.wp_url && !directWpSettings?.wp_url) {
      console.log("No WordPress URL available in settings:", {
        wpSettings: wpSettings?.wp_url,
        directWpSettings: directWpSettings?.wp_url,
      });
      return "#";
    }

    // Use either direct settings or context settings
    const baseUrl = wpSettings?.wp_url || directWpSettings?.wp_url || "";
    console.log("Base URL from settings:", baseUrl);

    // Make sure URL is properly formatted
    let formattedUrl = baseUrl;
    if (!formattedUrl.startsWith("http")) {
      formattedUrl = "https://" + formattedUrl;
      console.log("Added https protocol:", formattedUrl);
    }
    formattedUrl = formattedUrl.replace(/\/+$/, ""); // Remove trailing slashes
    console.log("Formatted URL after removing trailing slashes:", formattedUrl);

    // Log the constructed URL for debugging
    const adminUrl = `${formattedUrl}/wp-admin/post.php?post=${postId}&action=edit`;
    console.log("WordPress Admin URL constructed:", adminUrl);

    return adminUrl;
  };

  // Update WordPress publish status
  const updateWordPressPublishStatus = async (
    status: string,
    options?: { silent?: boolean }
  ) => {
    if (!currentWebsite) {
      toast.error("No website selected");
      return;
    }

    setWpPublishStatus(status);

    try {
      console.log("Updating WordPress publish status to:", status);

      const { error } = await supabase
        .from("wordpress_settings")
        .update({
          publish_status: status,
          updated_at: new Date().toISOString(),
        })
        .eq("website_id", currentWebsite.id);

      if (error) {
        console.error("Error updating WordPress publish status:", error);
        toast.error("Failed to update WordPress publish status");
        return;
      }

      if (!options?.silent) {
        toast.success("WordPress publish status updated successfully");
      }
    } catch (e) {
      console.error("Exception updating WordPress publish status:", e);
      toast.error("Failed to update WordPress publish status");
    }
  };

  // Debug function to directly check the database
  const debugCheckWordPressSettings = async () => {
    if (!currentWebsite) {
      toast.error("No website selected");
      return;
    }

    console.log("Directly checking WordPress settings in database...");
    console.log("Current website ID:", currentWebsite.id);

    try {
      const { data, error } = await supabase
        .from("wordpress_settings")
        .select("*")
        .eq("website_id", currentWebsite.id);

      if (error) {
        console.error("Error fetching WordPress settings from DB:", error);
        toast.error("Failed to check WordPress settings in database");
      } else if (data && data.length > 0) {
        console.log("WordPress settings found in database:", data);
        console.log("WordPress connection state:", data[0].is_connected);
        toast.success("Found WordPress settings in database - check console");
      } else {
        console.log("No WordPress settings found in database");
        toast.info("No WordPress settings found in database");
      }
    } catch (e) {
      console.error("Exception checking WordPress settings:", e);
      toast.error("Error checking WordPress settings");
    }
  };

  const testEdgeFunction = async (
    wpUrl: string,
    wpUsername: string,
    wpPassword: string
  ) => {
    console.log(`Testing Edge Function: ${WORDPRESS_PROXY_URL}`);
    console.log(
      `Using credentials - Username: ${wpUsername}, Password length: ${wpPassword.length}`
    );

    // Check password format - WordPress application passwords typically have spaces
    if (wpPassword.length > 10 && !wpPassword.includes(" ")) {
      console.warn(
        'Password does not contain spaces - WordPress application passwords typically have format "xxxx xxxx xxxx xxxx"'
      );
      toast.warning(
        "Your WordPress password may be in incorrect format. Application passwords should include spaces.",
        {
          duration: 8000,
        }
      );
    } else if (wpPassword.length < 24) {
      console.warn("Password length seems short for WordPress application password");
      toast.warning(
        "Your password seems shorter than typical WordPress application passwords.",
        {
          duration: 5000,
        }
      );
    }

    // Get current session for authentication
    const { data: sessionData } = await supabase.auth.getSession();

    // Prepare headers based on authentication status
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (sessionData?.session?.access_token) {
      console.log("Adding authentication to Edge Function request");
      headers["Authorization"] = `Bearer ${sessionData.session.access_token}`;
    } else {
      console.log("No authentication token available for Edge Function request");
      toast.error("You must be logged in to test WordPress connections");
      return false;
    }

    // Try multiple URL formats if main connection fails
    const cleanUrl = wpUrl.replace(/\/+$/, "");
    const urlVariations = [
      cleanUrl, // Original URL without trailing slashes
      cleanUrl.replace(/^https?:\/\//i, ""), // Without protocol
      cleanUrl.toLowerCase(), // Lowercase version
      `https://${cleanUrl.replace(/^https?:\/\//i, "")}`, // Force HTTPS
    ];

    console.log("Will try the following URL variations if needed:", urlVariations);

    // First try with the cleaned URL
    console.log(`Using WordPress URL: ${urlVariations[0]}`);

    try {
      // Log the exact data we're sending
      const requestPayload = {
        wpUrl: urlVariations[0],
        username: wpUsername,
        password: wpPassword, // Send password as-is, preserving any spaces
      };
      console.log("Sending payload to Edge Function:", {
        ...requestPayload,
        password: requestPayload.password.replace(/[^\s]/g, "x"), // Log masked password
      });

      // Send request to Edge Function
      const response = await fetch(WORDPRESS_PROXY_URL, {
        method: "POST",
        headers,
        body: JSON.stringify(requestPayload),
      });

      console.log(
        `Edge Function response status: ${response.status} ${response.statusText}`
      );

      try {
        const responseData = await response.json();
        console.log("Full response from Edge Function:", responseData);

        if (responseData.success) {
          toast.success("WordPress connection successful!");

          // Update the connection status in the database
          if (wpSettings && currentWebsite) {
            try {
              console.log("Updating connection status in WordPress settings");

              // Log current WordPress settings
              console.log("Current WordPress settings:", {
                id: wpSettings.id,
                website_id: wpSettings.website_id,
                is_connected: wpSettings.is_connected,
                updated_at: wpSettings.updated_at,
              });

              const { error } = await supabase
                .from("wordpress_settings")
                .update({
                  updated_at: new Date().toISOString(),
                  is_connected: true,
                })
                .eq("website_id", currentWebsite.id);

              if (error) {
                console.error("Error updating connection status:", error);
                console.error("Database error details:", {
                  code: error.code,
                  message: error.message,
                  details: error.details,
                  hint: error.hint,
                });
              } else {
                console.log("Successfully updated connection status");

                // Try to fetch and log the updated settings
                try {
                  const { data: updatedSettings, error: fetchError } = await supabase
                    .from("wordpress_settings")
                    .select("*")
                    .eq("website_id", currentWebsite.id)
                    .single();

                  if (fetchError) {
                    console.error("Failed to fetch updated settings:", fetchError);
                  } else {
                    console.log("Updated WordPress settings:", {
                      is_connected: updatedSettings.is_connected,
                      updated_at: updatedSettings.updated_at,
                    });
                  }
                } catch (fetchErr) {
                  console.error("Exception in fetching updated settings:", fetchErr);
                }

                // Refresh the page to show updated data
                toast.info("Connection status updated. Refreshing page...");
                setTimeout(() => {
                  window.location.reload();
                }, 1500);
              }
            } catch (updateError) {
              console.error("Exception updating connection status:", updateError);
            }
          }
          return true;
        } else {
          // Get specific error information
          const errorMsg = responseData.error || "Unknown error";
          const statusCode = responseData.statusCode || response.status;

          console.error(`WordPress connection failed (${statusCode}): ${errorMsg}`);

          // Show a more user-friendly error message based on status code
          if (statusCode === 401) {
            toast.error(
              `Authentication failed: Invalid WordPress username or application password`
            );
          } else if (statusCode === 404) {
            toast.error(
              `WordPress API not found: Check if the REST API is enabled on your site`
            );
          } else {
            toast.error(`WordPress connection failed: ${errorMsg}`);
          }

          // If we have settings but test failed, mark as not connected
          if (wpSettings && currentWebsite) {
            try {
              console.log("Updating connection status to false after failed test");

              const { error: updateError } = await supabase
                .from("wordpress_settings")
                .update({
                  is_connected: false,
                  updated_at: new Date().toISOString(),
                })
                .eq("website_id", currentWebsite.id);

              if (updateError) {
                console.error("Error updating connection status:", updateError);
              } else {
                console.log("Successfully marked WordPress connection as inactive");
                toast.info("The connection has been marked as inactive.");
              }
            } catch (updateError) {
              console.error("Error updating connection status:", updateError);
            }
          }

          return false;
        }
      } catch (parseError) {
        console.error("Could not parse Edge Function response", parseError);
        toast.error(
          `Could not parse Edge Function response: ${response.status} ${response.statusText}`
        );
        return false;
      }
    } catch (edgeError) {
      console.error("Edge Function error:", edgeError);
      toast.error(
        `Edge Function error: ${
          edgeError instanceof Error ? edgeError.message : String(edgeError)
        }`
      );
      return false;
    }
  };

  // Update the handleOpenWpFormat function to use the template from context
  const handleOpenWpFormat = () => {
    setWpFormatOpen(!wpFormatOpen);

    if (!wpFormatOpen) {
      console.log("Setting editor to current WordPress template from context");
      setHtmlTemplate(formattemplate);
    }
  };

  const handleWordPressConnect = async () => {
    setIsConnecting(true);
    try {
      handleStartWordPressAuth();
    } catch (error) {
      console.error("Error connecting to WordPress:", error);
      toast.error("Failed to connect to WordPress");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleWordPressDisconnect = async () => {
    // Add confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to disconnect WordPress? This will remove all WordPress connection settings."
    );
    if (!confirmed) {
      console.log("WordPress disconnect cancelled by user");
      return;
    }

    console.log("Starting WordPress disconnect process...");
    setIsConnecting(true);
    try {
      // First try to delete directly from the database if we have direct settings
      if (directWpSettings) {
        console.log(
          "Found direct WordPress settings, attempting direct database delete..."
        );
        const { error: deleteError } = await supabase
          .from("wordpress_settings")
          .delete()
          .eq("website_id", currentWebsite?.id);

        if (deleteError) {
          console.error("Error deleting WordPress settings directly:", deleteError);
          throw deleteError;
        }
        console.log("Successfully deleted WordPress settings directly from database");
      }

      // Then call the context disconnect function to clean up state
      console.log("Calling disconnect function from WordPress context...");
      const disconnected = await disconnectWordPress();
      console.log("Disconnect function returned:", disconnected);

      if (disconnected) {
        // Clear all WordPress-related state
        console.log("Clearing WordPress-related state...");
        setWpUrl("");
        setWpUsername("");
        setWpPassword("");
        setDirectWpSettings(null);
        setConnectionError(null);
        setConnectionDiagnostics(null);
        setWpPublishStatus("draft");
        toast.success("WordPress disconnected successfully");

        // Refresh the page to ensure all state is cleared
        console.log("Refreshing page to ensure clean state...");
        window.location.reload();
      } else {
        console.error("Disconnect function returned false");
        toast.error("Failed to disconnect from WordPress");
      }
    } catch (error) {
      console.error("Error disconnecting from WordPress:", error);
      toast.error(
        "Failed to disconnect from WordPress: " +
          (error instanceof Error ? error.message : "Unknown error")
      );
    } finally {
      setIsConnecting(false);
    }
  };

  // Memoize WordPress auth handlers
  const handleStartWordPressAuth = useCallback(() => {
    if (!currentWebsite?.url) {
      toast.error("Please enter your WordPress website URL first");
      return;
    }

    // Clear any previous errors when starting a new connection attempt
    setConnectionError(null);
    setConnectionDiagnostics(null);

    initiateWordPressAuth(currentWebsite.url);
    setShowAuthDialog(true);
  }, [currentWebsite, initiateWordPressAuth]);

  const handleDisconnectWordPress = async () => {
    // Add confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to disconnect WordPress? This will remove all WordPress connection settings."
    );
    if (!confirmed) {
      console.log("WordPress disconnect cancelled by user");
      return;
    }

    console.log("Starting WordPress disconnect process...");
    setIsConnecting(true);
    try {
      // First try to delete directly from the database if we have direct settings
      if (directWpSettings) {
        console.log(
          "Found direct WordPress settings, attempting direct database delete..."
        );
        const { error: deleteError } = await supabase
          .from("wordpress_settings")
          .delete()
          .eq("website_id", currentWebsite?.id);

        if (deleteError) {
          console.error("Error deleting WordPress settings directly:", deleteError);
          throw deleteError;
        }
        console.log("Successfully deleted WordPress settings directly from database");
      }

      // Then call the context disconnect function to clean up state
      console.log("Calling disconnect function from WordPress context...");
      const disconnected = await disconnectWordPress();
      console.log("Disconnect function returned:", disconnected);

      // Clear all WordPress-related state
      console.log("Clearing WordPress-related state...");
      setWpUrl("");
      setWpUsername("");
      setWpPassword("");
      setDirectWpSettings(null);
      setConnectionError(null);
      setConnectionDiagnostics(null);
      setWpPublishStatus("draft");

      toast.success("WordPress disconnected successfully");

      // Refresh the page to ensure clean state
      console.log("Refreshing page to ensure clean state...");
      window.location.reload();
    } catch (error) {
      console.error("Error disconnecting from WordPress:", error);
      toast.error(
        "Failed to disconnect from WordPress: " +
          (error instanceof Error ? error.message : "Unknown error")
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleCompleteWordPressAuth = async () => {
    // Field validations
    if (!wpUsername.trim()) {
      setConnectionError("WordPress username is required");
      return;
    }

    if (!wpPassword.trim()) {
      setConnectionError("WordPress password is required");
      return;
    }

    if (!currentWebsite) {
      toast.error("No website selected. Please select a website first");
      console.log("Error: No website selected for WordPress authentication");
      return;
    }

    // Clear previous error states when starting authentication
    setConnectionError(null);
    setConnectionDiagnostics(null);
    setIsAuthenticating(true);
    try {
      // Clean and validate the URL
      let url = currentWebsite.url.trim();

      // Make sure the URL is properly formatted
      if (!url.match(/^https?:\/\//)) {
        url = "https://" + url;
        console.log("URL adjusted to include protocol:", url);
      }

      // Remove any trailing slashes
      url = url.replace(/\/+$/, "");

      console.log("Attempting direct WordPress auth for website:", url);

      // Try direct connection with basic auth credentials
      // Use a URL that's known to work (WordPress.org) to store our credentials directly
      console.log("Trying direct WordPress authentication...");

      // Create credentials for saving to database
      const wpSettings = {
        website_id: currentWebsite.id,
        wp_url: url,
        wp_username: wpUsername,
        wp_application_password: wpPassword,
        is_connected: true,
        updated_at: new Date().toISOString(),
      };

      // Save to database without testing (trust user input)
      console.log("Saving WordPress settings directly to database");
      try {
        // Check if there's already a record for this website
        const { data: existingSettings, error: fetchError } = await supabase
          .from("wordpress_settings")
          .select("*")
          .eq("website_id", currentWebsite.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (fetchError && fetchError.code !== "PGRST116") {
          // Real error (not "no rows returned" error)
          console.error("Error checking for existing settings:", fetchError);
          toast.error("Failed to check for existing WordPress settings");
          setConnectionError("Database error: " + fetchError.message);
          return;
        }

        let result;
        if (existingSettings && existingSettings.length > 0) {
          // Update existing record
          console.log("Updating existing WordPress settings");
          result = await supabase
            .from("wordpress_settings")
            .update(wpSettings)
            .eq("website_id", currentWebsite.id)
            .select()
            .order('created_at', { ascending: false })
            .limit(1);
        } else {
          // Insert new record
          console.log("Inserting new WordPress settings");
          result = await supabase
            .from("wordpress_settings")
            .insert(wpSettings)
            .select()
            .order('created_at', { ascending: false })
            .limit(1);
        }

        const { data, error } = result;

        if (error) {
          console.error("Database error:", error);
          toast.error("Failed to save WordPress settings");
          setConnectionError("Database error: " + error.message);
          return;
        }

        // Update the directWpSettings state with the new data
        if (data && data.length > 0) {
          setDirectWpSettings(data[0]);
        }

        setShowAuthDialog(false);
        setWpUsername("");
        setWpPassword("");
        toast.success("WordPress connection saved successfully");
        console.log("WordPress settings saved to database");

        // Offer to test the connection right after saving
        const testConnection = window.confirm(
          "WordPress connection saved. Would you like to test the connection now?"
        );
        if (testConnection) {
          // Test the connection
          try {
            console.log(`Testing WordPress connection to: ${url}`);

            // Show notice about development environment
            if (window.location.hostname === "localhost") {
              toast.info(
                "Note: Connection tests may return detailed errors to help with troubleshooting",
                {
                  duration: 6000, // Show for 6 seconds
                }
              );
            }

            // Use our direct test function with the Edge Function
            if (
              wpSettings &&
              wpSettings.wp_username &&
              wpSettings.wp_application_password
            ) {
              await testEdgeFunction(
                wpSettings.wp_url,
                wpSettings.wp_username,
                wpSettings.wp_application_password
              );
            } else {
              toast.error("WordPress credentials not found");
            }
          } catch (testError) {
            console.error("Connection test error:", testError);
            toast.error(
              `Connection test failed: ${
                testError instanceof Error ? testError.message : String(testError)
              }`
            );

            // Still show a positive message
            toast.info(
              `Your WordPress connection details are saved and may work for publishing even if testing fails.`
            );
          }
        }
      } catch (dbError) {
        console.error("Database operation error:", dbError);
        toast.error("Error saving WordPress settings to the database");
        setConnectionError(
          "Database error: " +
            (dbError instanceof Error ? dbError.message : String(dbError))
        );
      }
    } catch (error) {
      console.error("Error during WordPress authentication:", error);
      toast.error(
        "Failed to connect to WordPress. Please check your credentials and URL."
      );
      setConnectionError(
        "Authentication error: " +
          (error instanceof Error ? error.message : String(error))
      );
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Memoize the main settings save handler
  const handleSave = useCallback(async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first", {
        id: "website-required",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateSettingsInDatabase(
        postingFrequency,
        styleInput,
        subjects,
        formattemplate,
        imagePrompt,
        imageModel,
        negativePrompt,
        weeklyPlanningDay,
        contentPolicies
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving settings:", error);
      // Error toast is handled by the context
    } finally {
      setIsSaving(false);
    }
  }, [
    currentWebsite,
    postingFrequency,
    styleInput,
    subjects,
    formattemplate,
    imagePrompt,
    imageModel,
    negativePrompt,
    weeklyPlanningDay,
    updateSettingsInDatabase,
    contentPolicies,
  ]);

  // Add direct fetch from database when dialog opens
  useEffect(() => {
    const fetchWpSettingsDirectly = async () => {
      if (showAuthDialog && currentWebsite) {
        console.log(
          "Dialog opened, fetching WordPress settings directly from the database"
        );

        try {
          console.log("Making direct Supabase call to fetch WordPress settings");
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) {
            console.error("No active session found");
            return;
          }

          const { data, error } = await supabase
            .from("wordpress_settings")
            .select("*")
            .eq("website_id", currentWebsite.id)
            .order("created_at", { ascending: false })
            .limit(1);

          if (error) {
            console.error("Error fetching WordPress settings:", error);
            return;
          }

          if (data && data.length > 0) {
            console.log("Directly fetched settings from database:", data[0]);
            setDirectWpSettings(data[0]);
            // Only set fields if they're empty
            if (!wpUsername) {
              setWpUsername(data[0].wp_username || "");
            }
            if (!wpPassword) {
              setWpPassword(data[0].wp_application_password || "");
            }
          }
        } catch (error) {
          console.error("Error fetching WordPress settings:", error);
        }
      }
    };

    fetchWpSettingsDirectly();
  }, [showAuthDialog, currentWebsite]);

  // The existing useEffect for dialog open state
  useEffect(() => {
    console.log("Dialog open state changed:", showAuthDialog);
    console.log("WordPress settings from context:", wpSettings);

    if (showAuthDialog && wpSettings) {
      console.log("Dialog opened with existing settings, trying to populate fields:", {
        username: wpSettings.wp_username,
        passwordLength: wpSettings.wp_application_password?.length || 0,
      });

      // Only set fields if they're empty
      if (!wpUsername) {
        setWpUsername(wpSettings.wp_username || "");
      }
      if (!wpPassword) {
        setWpPassword(wpSettings.wp_application_password || "");
      }
    }
  }, [showAuthDialog, wpSettings]);

  // First fix useEffect to fetch WordPress settings with proper table name
  useEffect(() => {
    const fetchWordPressSettings = async () => {
      if (!currentWebsite) return;

      console.log(
        "Directly checking WordPress settings in database for website:",
        currentWebsite.id
      );

      try {
        const { data, error } = await supabase
          .from("wordpress_settings")
          .select("*")
          .eq("website_id", currentWebsite.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          if (error.code !== "PGRST116") {
            // Not the "no rows returned" error
            console.error("Error fetching WordPress settings from DB:", error);
          } else {
            console.log("No WordPress settings found in database for this website");
            setDirectWpSettings(null);
          }
        } else if (data && data.length > 0) {
          console.log("WordPress settings found in database:", data[0]);

          // Store the direct WordPress settings in state
          setDirectWpSettings(data[0]);

          // Set publish status if it exists
          if (data[0]?.publish_status) {
            setWpPublishStatus(data[0].publish_status);
          }
        } else {
          setDirectWpSettings(null);
        }
      } catch (e) {
        console.error("Exception checking WordPress settings:", e);
        setDirectWpSettings(null);
      }
    };

    fetchWordPressSettings();
  }, [currentWebsite]);

  // Replace with the original useEffect for getting the current website ID
  useEffect(() => {
    if (currentWebsite) {
      console.log("Current website ID:", currentWebsite.id);
      console.log("Checking WordPress settings...");

      // Direct check in the database
      const checkWpSettings = async () => {
        try {
          const { data, error } = await supabase
            .from("wordpress_settings")
            .select("*")
            .eq("website_id", currentWebsite.id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (error) {
            console.error("Error checking WordPress settings:", error);
            setDirectWpSettings(null);
          } else if (data && data.length > 0) {
            console.log("WordPress settings found directly in DB:", data[0]);
            console.log("WordPress connection state:", data[0]?.is_connected);

            // Store the WordPress settings directly in state
            setDirectWpSettings(data[0]);

            // Set publish status if it exists
            if (data[0]?.publish_status) {
              setWpPublishStatus(data[0].publish_status);
            }
          } else {
            console.log("No WordPress settings found");
            setDirectWpSettings(null);
          }
        } catch (e) {
          console.error("Exception checking WordPress settings:", e);
          setDirectWpSettings(null);
        }
      };

      checkWpSettings();
    }
  }, [currentWebsite]);

  // Update the useEffect dependencies to include directWpSettings
  useEffect(() => {
    console.log("=== WordPress Integration Status Debug ===");
    console.log("Component mounted or WordPress settings changed");

    // Log direct DB settings
    console.log(
      "WordPress settings (direct DB):",
      directWpSettings
        ? {
            has_settings: true,
            wp_url: directWpSettings.wp_url?.substring(0, 30) + "...",
            has_username: !!directWpSettings.wp_username,
            has_password: !!directWpSettings.wp_application_password,
            is_connected: directWpSettings.is_connected,
            website_id: directWpSettings.website_id,
          }
        : "No direct settings available"
    );

    // Log context settings
    console.log(
      "WordPress settings (context):",
      wpSettings
        ? {
            has_settings: true,
            wp_url: wpSettings.wp_url?.substring(0, 30) + "...",
            has_username: !!wpSettings.wp_username,
            has_password: !!wpSettings.wp_application_password,
            is_connected: wpSettings.is_connected,
            website_id: wpSettings.website_id,
          }
        : "No settings available"
    );

    console.log("isWordPressConfigured():", isWordPressConfigured());
    console.log(
      "Current website:",
      currentWebsite
        ? {
            id: currentWebsite.id,
            name: currentWebsite.name,
          }
        : "No website selected"
    );

    console.log("=== End WordPress Integration Status Debug ===");
  }, [wpSettings, directWpSettings, currentWebsite]);

  // Explicitly log WordPress settings from context when they change
  useEffect(() => {
    console.log("WordPress settings from context changed:", wpSettings);
  }, [wpSettings]);

  // Add effect to track changes with debounce
  useEffect(() => {
    if (settingsLoading || !hasUnsavedChanges) return;

    const timer = setTimeout(() => {
      handleSave();
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [hasUnsavedChanges, handleSave, settingsLoading]);

  return {
    wordPressSettingsSection,
    isWordPressConfigured,
    isWordPressConnected,
    refreshWordPressSettings,
    getWordPressEditUrl,
    updateWordPressPublishStatus,
    debugCheckWordPressSettings,
    testEdgeFunction,
    handleOpenWpFormat,
    handleWordPressConnect,
    handleWordPressDisconnect,
    handleStartWordPressAuth,
    handleDisconnectWordPress,
    handleCompleteWordPressAuth,
    handleSave,
  };
}
